define("shared/utils/find-link-segments", ["exports"], function (_exports) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.default = void 0;
  0; //eaimeta@70e063a35619d71feaimeta@70e063a35619d71f
  // Shared URL-matching core behind the `linkify-text` and `linkify-html` helpers.
  // Mirrors Later Influence's `linkify-core` util.

  // Matches an http(s):// URL or a bare `www.` host. /i covers `HTTP://` and `WWW.`, and /g
  // lets `exec()` walk the string. The inner lookahead stops a match at the next scheme so
  // `https://a.com/x,https://b.com` (two links pasted with no space) doesn't swallow the
  // second into the first and emit one dead anchor. Deliberately still allows commas, so
  // URLs that legitimately contain them (map coordinates) survive intact.
  const URL_REGEX = /(?:https?:\/\/|www\.)(?:(?!https?:\/\/)\S)+/gi;

  // Punctuation and quotes that commonly trail URLs in prose but aren't part of the URL,
  // e.g. the period in "Check out https://later.com." — we strip these off the end of each
  // match so the link's href doesn't include them. Quotes matter more than they look: a
  // trailing one lands in the *hostname*, so `https://later.com"` resolves to the host
  // `later.com"` and the link is simply dead.
  const TRAILING_PUNCTUATION_REGEX = /[.,;:!?)\]}>'"`”’»]+$/;
  const HAS_SCHEME_REGEX = /^https?:\/\//i;

  // Characters that mean the match landed inside a larger token rather than at the start of
  // a URL: `foo-http://evil.com`, `ftp://www.x`, `steve@www.x`, `shot-www.png`, `#www.x`.
  // `:` is deliberately absent so `Link:https://later.com` keeps working.
  const MID_TOKEN_CHAR_REGEX = /[\w@./\-+~%#]/;

  // Hosts are letters, digits, dots and hyphens (IDN arrives here already punycoded).
  // Anything else means we mis-sliced, e.g. `www.a.com,www.b.com` parsing as one host.
  const VALID_HOSTNAME_REGEX = /^[a-z0-9.-]+$/i;
  /**
   * Resolves a matched substring to an href, or undefined when it isn't a usable http(s) URL.
   * A bare `www.` match gets https:// so the browser doesn't read the href as a relative path.
   * `new URL()` throws on inputs the parser rejects (e.g. malformed IPv6), so we wrap it;
   * undefined means "render as plain text" rather than blow up the tree.
   */
  const toHref = candidate => {
    const hasScheme = HAS_SCHEME_REGEX.test(candidate);
    const withScheme = hasScheme ? candidate : `https://${candidate}`;
    try {
      const parsed = new URL(withScheme);

      // `www.later.com@evil.com` parses as userinfo plus host evil.com, so the text reads as
      // one domain while the link goes to another. Refuse it rather than render the anchor.
      if (parsed.username || parsed.password) {
        return undefined;
      }
      if (!VALID_HOSTNAME_REGEX.test(parsed.hostname)) {
        return undefined;
      }

      // A host we guessed the scheme for has to at least look like a domain. Drops the
      // `https://www` left behind when trailing punctuation is peeled off `www.,`
      if (!hasScheme && !parsed.hostname.includes('.')) {
        return undefined;
      }
      return withScheme;
    } catch {
      return undefined;
    }
  };

  /**
   * Splits a string into an ordered list of text and link segments. Concatenating every
   * segment's text yields the input back unchanged; only the href gets a scheme filled in.
   *
   * Approach:
   *   for each URL the regex finds in `text`:
   *     1. peel off any trailing punctuation or quotes
   *     2. skip it if the match started mid-token, so it isn't really the start of a URL
   *     3. if what's left resolves to an http(s) href, emit: [prior text, link, trailing]
   *        else emit the match unchanged as text
   *   then, emit whatever text remains after the last match
   */
  const findLinkSegments = text => {
    const segments = [];
    let lastIndex = 0; // end of the most recently consumed slice of `text`

    // Reset the regex's internal cursor — /g regexes are stateful across exec() calls
    URL_REGEX.lastIndex = 0;
    let match = URL_REGEX.exec(text);
    while (match !== null) {
      const [fullMatch] = match;
      const startIndex = match.index;
      const trailing = TRAILING_PUNCTUATION_REGEX.exec(fullMatch)?.[0] ?? '';
      const candidate = trailing ? fullMatch.slice(0, fullMatch.length - trailing.length) : fullMatch;
      if (startIndex > lastIndex) {
        segments.push({
          kind: 'text',
          value: text.slice(lastIndex, startIndex)
        });
      }
      const atTokenStart = startIndex === 0 || !MID_TOKEN_CHAR_REGEX.test(text[startIndex - 1] ?? '');
      const href = atTokenStart ? toHref(candidate) : undefined;
      if (href) {
        segments.push({
          kind: 'link',
          href,
          text: candidate
        });
        if (trailing) {
          segments.push({
            kind: 'text',
            value: trailing
          });
        }
      } else {
        // Regex matched something the URL parser rejected, so render verbatim
        segments.push({
          kind: 'text',
          value: fullMatch
        });
      }
      lastIndex = startIndex + fullMatch.length;
      match = URL_REGEX.exec(text);
    }

    // Tail: any text after the final URL match (or all of `text` if no matches)
    if (lastIndex < text.length) {
      segments.push({
        kind: 'text',
        value: text.slice(lastIndex)
      });
    }
    return segments;
  };
  var _default = _exports.default = findLinkSegments;
});